import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import Button, { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import clsx from 'clsx';
import { forwardRef } from 'react';

export type StyledButtonLinkProps = ButtonProps & {
  /** target for anchor tag */
  target?: string;
  /** href for anchor tag */
  href?: string;
  /** rel for anchor tag. If target === '_blank', rel defaults to noopener noreferrer */
  rel?: string;
  /** Optional pill formatting */
  pill?: boolean;
  /** Optional transform all UPPERCASE to Normal Case */
  normalCase?: boolean;
  /** Optional replace label with circular progress */
  isPending?: boolean;
  /** Optional label when isPending === true */
  isPendingLabel?: string;
  /** Optional. If true, button will not have background color on hover or ripple */
  link?: boolean;
};

const StyledCircularProgress = styled(CircularProgress)(() => ({
  color: 'inherit',
}));

// Move this outside the component to avoid recreation on each render
function IsPendingButtonLabel({ isPendingLabel }: { isPendingLabel?: string }) {
  return (
    <Box display='flex' gap={2} alignItems='center'>
      {isPendingLabel ? isPendingLabel : null}
      <StyledCircularProgress size={24} />
    </Box>
  );
}

/** Primary UI component for user interaction */
export const StyledButtonLinkWithRef = forwardRef<
  HTMLButtonElement,
  StyledButtonLinkProps
>(
  (
    {
      target,
      href,
      rel = target === '_blank' ? 'noopener noreferrer' : undefined,
      pill = false,
      normalCase = false,
      isPending = false,
      isPendingLabel,
      link = false,
      className,
      ...restProps
    }: StyledButtonLinkProps,
    ref,
  ) => {
    return (
      <Button
        component='a'
        target={target}
        href={href}
        rel={rel}
        ref={ref}
        className={clsx(
          {
            pill,
            'button-normal-case': normalCase,
          },
          className,
        )}
        sx={{
          ...(link && {
            boxShadow: 'unset',
            '&:hover': {
              backgroundColor: restProps.variant === 'text' && 'transparent',
            },
          }),
        }}
        disableRipple={link}
        {...restProps} // restProps only contains valid Button props now
      >
        {isPending ? (
          <IsPendingButtonLabel isPendingLabel={isPendingLabel} />
        ) : (
          restProps.children
        )}
      </Button>
    );
  },
);

StyledButtonLinkWithRef.displayName = 'StyledButton';

export const StyledButtonLink = StyledButtonLinkWithRef as <
  C extends React.ElementType = 'button',
>(
  props: StyledButtonLinkProps & {
    component?: C;
  } & Omit<React.ComponentPropsWithoutRef<C>, keyof StyledButtonLinkProps>,
) => React.ReactElement | null;
